/**
 * One signature box for every placement (Pre-Defined, Drag & Drop, Default) and
 * every method (Electronic, Aadhaar, DSC).
 *
 * Sizes are in PDF points, the unit sent in `position_details`. Every page is
 * shown on screen at 4/3 px per point (see pdf-virtual-viewer), so this box is
 * about 147 x 67 px on screen.
 *
 * 110pt is the widest box that still fits the five-column Pre-Defined bottom row
 * on an A4 page (595pt wide) without neighbouring signatures overlapping.
 */
export const SIGNATURE_BOX_WIDTH_PT = 110;
export const SIGNATURE_BOX_HEIGHT_PT = 50;

/** CSS px per PDF point — the scale every PDF page is rendered at. */
export const PX_PER_PT = 4 / 3;

export const SIGNATURE_BOX_WIDTH_PX = SIGNATURE_BOX_WIDTH_PT * PX_PER_PT;
export const SIGNATURE_BOX_HEIGHT_PX = SIGNATURE_BOX_HEIGHT_PT * PX_PER_PT;

export interface SignatureBoxRect {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

/**
 * Moves a box back inside the page *without resizing it*. Clamping each edge on
 * its own (`x2 = min(x2, pageWidth)`) quietly shrinks a box that sits near an
 * edge, which is one of the ways signatures ended up in different sizes.
 *
 * Only a page smaller than the box itself can still shrink it.
 */
export function fitSignatureBoxInPage(
  x1: number,
  y1: number,
  width: number,
  height: number,
  pageWidth: number,
  pageHeight: number,
): SignatureBoxRect {
  const boxWidth = Math.min(width, pageWidth);
  const boxHeight = Math.min(height, pageHeight);
  const left = Math.round(Math.max(0, Math.min(x1, pageWidth - boxWidth)));
  const top = Math.round(Math.max(0, Math.min(y1, pageHeight - boxHeight)));
  return { x1: left, x2: left + boxWidth, y1: top, y2: top + boxHeight };
}

/**
 * On-screen size of a stored signature box, taken from its own coordinates so an
 * order is always shown at the size it will really be signed at. Falls back to the
 * standard box when the coordinates don't describe a rectangle.
 */
export function getSignatureBoxSizePx(box: any): { width: number; height: number } {
  const width = Number(box?.x2) - Number(box?.x1);
  const height = Number(box?.y2) - Number(box?.y1);
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { width: width * PX_PER_PT, height: height * PX_PER_PT };
  }
  return { width: SIGNATURE_BOX_WIDTH_PX, height: SIGNATURE_BOX_HEIGHT_PX };
}

/**
 * How tall a stamp actually prints inside a SIGNATURE_BOX_HEIGHT_PT box: the
 * signature is drawn at roughly 60% of the box height, resting on its bottom
 * edge, for Aadhaar, DSC and Electronic alike. Used to stack the two Default
 * rows so the second row's signature sits just above the first row's.
 */
export const PRINTED_STAMP_HEIGHT_PT = 30;
